/**
 * FORM SELECT COMPONENT
 *
 * Reusable select dropdown with consistent styling.
 * Supports labels, help text, and error states.
 *
 * Features:
 * - Dark/light variants
 * - Label and help text
 * - Error state with message
 * - Typed options
 *
 * @example
 * <FormSelect
 *   label="Category"
 *   value={category}
 *   onChange={(e) => setCategory(e.target.value)}
 *   options={[
 *     { value: 'polish', label: 'Nail Polish' },
 *     { value: 'plate', label: 'Stamping Plate' },
 *   ]}
 * />
 */

import { type SelectHTMLAttributes, forwardRef } from 'react';

export type SelectVariant = 'light' | 'dark';

export interface SelectOption {
  value: string;
  label: string;
  disabled?: boolean;
}

export interface FormSelectProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'size'> {
  /** Label text above select */
  label?: string;
  /** Help text below select */
  helpText?: string;
  /** Error message (also sets error styling) */
  error?: string;
  /** Color variant */
  variant?: SelectVariant;
  /** Select options */
  options: SelectOption[];
  /** Placeholder option (first option, usually disabled) */
  placeholder?: string;
  /** Additional class for wrapper */
  wrapperClassName?: string;
  /** Additional class for select */
  className?: string;
}

const variantClasses: Record<SelectVariant, { select: string; label: string; help: string }> = {
  light: {
    select: 'border-gray-300 bg-white text-gray-900 focus:ring-purple-500 focus:border-purple-500',
    label: 'text-gray-700',
    help: 'text-gray-500',
  },
  dark: {
    select: 'border-slate-600 bg-slate-700/50 text-glass focus:ring-purple-500 focus:border-transparent',
    label: 'text-glass-secondary',
    help: 'text-glass-muted',
  },
};

export const FormSelect = forwardRef<HTMLSelectElement, FormSelectProps>(
  (
    {
      label,
      helpText,
      error,
      variant = 'light',
      options,
      placeholder,
      wrapperClassName = '',
      className = '',
      id,
      ...props
    },
    ref
  ) => {
    const selectId = id || label?.toLowerCase().replace(/\s+/g, '-');
    const styles = variantClasses[variant];
    const hasError = !!error;

    const selectClasses = [
      'w-full rounded-lg border px-3 py-2 transition-colors',
      'focus:outline-none focus:ring-2',
      styles.select,
      hasError ? 'border-red-500 focus:ring-red-500' : '',
      className,
    ]
      .filter(Boolean)
      .join(' ');

    return (
      <div className={wrapperClassName}>
        {label && (
          <label
            htmlFor={selectId}
            className={`block text-sm font-medium mb-1 ${styles.label}`}
          >
            {label}
            {props.required && <span className="text-red-500 ml-1">*</span>}
          </label>
        )}

        <select
          ref={ref}
          id={selectId}
          className={selectClasses}
          aria-invalid={hasError}
          aria-describedby={error ? `${selectId}-error` : helpText ? `${selectId}-help` : undefined}
          {...props}
        >
          {placeholder && (
            <option value="" disabled>
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} disabled={option.disabled}>
              {option.label}
            </option>
          ))}
        </select>

        {error && (
          <p id={`${selectId}-error`} className="mt-1 text-sm text-red-500">
            {error}
          </p>
        )}

        {helpText && !error && (
          <p id={`${selectId}-help`} className={`mt-1 text-sm ${styles.help}`}>
            {helpText}
          </p>
        )}
      </div>
    );
  }
);

FormSelect.displayName = 'FormSelect';

export default FormSelect;
